import { IconButton, Text, TextInput } from "@carbon6/c6-frontend-design";
import { useMemo, useState, type ChangeEvent, type ReactNode } from "react";
import { type ReservedKeyContract } from "@/api";
import { hatcheryTextModes } from "@/app/theme";
import { EmptyState } from "@/shared/ui/emptyState/public";
import { LoadingState } from "@/shared/ui/loadingState/public";
import { HARDWARE_AXIS_KEYS } from "./hardwareKeys";
import { HatLabelChip } from "./HatLabelChip";
import { filterExistingLabels } from "./labelPicker";
import { NewLabelDialog } from "./NewLabelDialog";
import { useLabelCatalog, type LabelCatalogStatus } from "./useLabelCatalog";
import styles from "./AddLabelRow.module.css";

// 기존 라벨 검색 결과를 너무 길지 않게 보여준다.
const MAX_PICKER_MATCHES = 12;

export type AddLabelRowProps = {
  // 예약 key 계약(environment/source/task). 기존 라벨 검색에서 단일값 key 를 숨기는 데 쓴다.
  contracts: readonly ReservedKeyContract[];
  busy: boolean;
  // (key, value) 한 건을 추가한다. 기존 라벨 선택과 새 라벨 만들기가 같은 경로로 호출한다.
  onAdd: (key: string, value: string) => Promise<void>;
  // 추가를 막을 key 집합(기본값은 하드웨어 축). label 로 받지 않는 축을 클라이언트에서 먼저 막는다.
  blockedKeys?: readonly string[];
  // 이미 부여된 라벨의 key 목록. 단일값 key(environment/source)가 여기 있으면 검색에서 숨긴다.
  assignedKeys?: readonly string[];
  // 이미 부여된 (key,value) 목록. 기존 라벨 검색 결과에서 중복을 뺀다. 없으면 빼지 않는다.
  assignedLabels?: readonly { key: string; value: string }[];
};

/**
 * 라벨 추가 행. **고르기와 만들기를 구분한다.**
 *
 * 행에는 기존 라벨 검색 하나만 둔다. 대부분의 추가는 이미 있는 라벨을 부여하는 일이다.
 * 새 라벨은 카탈로그에 남아 다른 데이터셋에도 표시된다. 그래서 무거운 일로 보고 창
 * (`NewLabelDialog`)으로 분리한다.
 *
 * 이 행이 카탈로그를 한 번 적재하고, 검색과 창이 함께 쓴다. 창이 따로 조회하면 창을 열 때마다
 * 같은 목록을 다시 받는다.
 */
export function AddLabelRow({
  contracts,
  busy,
  onAdd,
  blockedKeys = HARDWARE_AXIS_KEYS,
  assignedKeys = [],
  assignedLabels = [],
}: AddLabelRowProps): ReactNode {
  // 기존 라벨 검색어. 카탈로그 전체에서 고른다.
  const [pickerQuery, setPickerQuery] = useState("");
  const [creating, setCreating] = useState(false);
  const { catalog, status: catalogStatus } = useLabelCatalog();

  // 단일값 reserved key. 이미 부여되었으면 검색 결과에서 숨긴다. 정책이 SoT 다. allowlist 는
  // 단일값, open(task)은 다중값으로 본다. 서버는 개수를 강제하지 않는다(확정에서 예약 라벨은
  // optional). 그래서 이 규칙은 입력 UX 결정이다.
  const singleValueKeys = useMemo(
    () => contracts.filter((entry) => entry.policy === "allowlist").map((entry) => entry.key),
    [contracts],
  );

  // 기존 라벨 검색 결과. key 를 먼저 고르지 않고 카탈로그 전체에서 바로 추가한다.
  // 결과에서 빼는 것:
  //   - 추가할 수 없는 key(하드웨어 축, 이미 부여된 단일값 reserved key)
  //   - 이미 부여된 (key,value)
  const pickerMatches = useMemo(
    () =>
      filterExistingLabels(catalog, pickerQuery, {
        blockedKeys,
        hiddenKeys: singleValueKeys.filter((key) => assignedKeys.includes(key)),
        assigned: assignedLabels,
        limit: MAX_PICKER_MATCHES,
      }),
    [catalog, pickerQuery, blockedKeys, assignedKeys, assignedLabels, singleValueKeys],
  );

  return (
    <div className={styles.addRow}>
      <Text variant={hatcheryTextModes["caption-bold"]} className={styles.muted}>
        라벨 추가
      </Text>
      {/* 기존 라벨 검색·선택. 카탈로그 전체에서 바로 부여한다. 새 라벨 만들기와 구분된다. */}
      <div className={styles.picker}>
        <TextInput
          value={pickerQuery}
          placeholder="기존 라벨을 key:value 형식으로 검색해 주세요."
          aria-label="기존 라벨 검색"
          fullWidth
          disabled={busy}
          onChange={(event: ChangeEvent<HTMLInputElement>) => setPickerQuery(event.target.value)}
        />
        {pickerQuery.trim() !== "" ? (
          pickerMatches.length > 0 ? (
            <div className={styles.pickerMatches}>
              {pickerMatches.map((match) => (
                <button
                  key={match.uuid}
                  type="button"
                  className={styles.pickerChip}
                  disabled={busy}
                  onClick={() => void onAdd(match.key, match.value)}
                >
                  <HatLabelChip
                    labelKey={match.key}
                    value={match.value}
                    color={match.color}
                    size="xs"
                  />
                </button>
              ))}
            </div>
          ) : // 적재가 끝나기 전에는 "없습니다" 로 단정하지 않는다. 그 말은 사실이 아니다. 곧
          // 표시될 칩이 있는데 사용자에게 새로 만들라고 유도하게 된다.
          catalogStatus === "loading" ? (
            <LoadingState size="sm" mode="plain">
              {pickerEmptyMessage(catalogStatus)}
            </LoadingState>
          ) : (
            <EmptyState size="sm" mode="plain">
              {pickerEmptyMessage(catalogStatus)}
            </EmptyState>
          )
        ) : null}
      </div>

      {/* 만들기는 카탈로그에 남는 일이라 창으로 분리한다. 고르기와 같은 무게로 두지 않는다. */}
      <div className={styles.createRow}>
        <IconButton
          text="새 라벨 만들기"
          size="sm"
          variant="surface"
          disabled={busy}
          onClick={() => setCreating(true)}
        />
      </div>

      <NewLabelDialog
        open={creating}
        busy={busy}
        catalog={catalog}
        catalogStatus={catalogStatus}
        blockedKeys={blockedKeys}
        onCreate={onAdd}
        onClose={() => setCreating(false)}
      />
    </div>
  );
}

// 검색 결과가 빈 이유를 상태별로 구분한다. "없다" 는 카탈로그를 다 본 뒤에만 말할 수 있다.
function pickerEmptyMessage(status: LabelCatalogStatus): string {
  if (status === "loading") {
    return "기존 라벨을 불러오고 있습니다.";
  }
  if (status === "failed") {
    return "기존 라벨을 확인하지 못했습니다. 새 라벨로 만들 수는 있습니다.";
  }
  return "일치하는 기존 라벨이 없습니다. 새 라벨로 만들어 주세요.";
}
